import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  AvailableIntegration,
  availableIntegrations
} from '@pages/AdminSettings/components/IntegrationTab/constants';
import {
  Alert,
  Button,
  Card,
  Empty,
  Modal,
  Popconfirm,
  Skeleton,
  Tag,
  Typography
} from 'antd';
import { CloseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import Fuse from 'fuse.js';
import { IntegrationItemDto } from '@api/Api';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@api/client';

const { Title, Paragraph, Text } = Typography;

interface IntegrationCardProps {
  integration: IntegrationItemDto & {
    typeName: string;
    description: string;
    logo?: AvailableIntegration['logo'];
    tags: AvailableIntegration['tags'];
    details?: AvailableIntegration['installedDetails'];
  };
  onDetailsClick: () => void;
  onErrorClick: () => void;
  refetchInstalledIntegrations: () => Promise<void>;
}

function IntegrationCard({
  integration,
  onDetailsClick,
  onErrorClick,
  refetchInstalledIntegrations
}: IntegrationCardProps) {
  const [name, setName] = useState(integration.name || '');
  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      return await apiClient.integration.integrationControllerRenameIntegration(
        { id: integration.id, name }
      );
    }
  });
  useEffect(() => {
    setName(integration.name || '');
  }, [integration.name]);

  const uninstallMutation = useMutation({
    mutationFn: async () => {
      return await apiClient.integration.integrationControllerUninstallIntegration(
        { id: integration.id }
      );
    },
    onSettled: async () => {
      await refetchInstalledIntegrations();
    }
  });
  return (
    <Card
      title={
        name ? (
          <Text
            onClick={(e) => e.stopPropagation()}
            editable={{
              maxLength: 255,
              text: name,
              tooltip: 'Click to edit integration name',
              triggerType: ['text', 'icon'],
              onChange: (v) => {
                const newName = (v || name || '').trim();
                if (newName === name) return;
                setName(newName);
                renameMutation.mutate(newName);
              }
            }}
          >
            {name}
          </Text>
        ) : null
      }
      extra={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {integration.status === 'error' ? (
            <Tag
              icon={<CloseCircleOutlined />}
              color="error"
              role="button"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onErrorClick();
              }}
            >
              Error
            </Tag>
          ) : null}
          <div onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              title="Are you sure you want to uninstall this integration?"
              onConfirm={() => uninstallMutation.mutate()}
            >
              <Button
                danger
                type="dashed"
                size="small"
                loading={uninstallMutation.isLoading}
              >
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </div>
        </div>
      }
      style={{ height: '100%' }}
      hoverable={!!integration.details}
      onClick={onDetailsClick}
    >
      <Card.Meta
        title={integration.typeName}
        description={
          <>
            <Paragraph>
              {integration.tags.map((x) => (
                <Tag key={x} style={{ marginTop: 8 }}>
                  {x}
                </Tag>
              ))}
            </Paragraph>
            <Paragraph>{integration.description}</Paragraph>
          </>
        }
        avatar={
          integration.logo ? <integration.logo width={64} height={64} /> : null
        }
      />
    </Card>
  );
}

interface InstalledIntegrationsProps {
  searchQuery: string;
  list: IntegrationItemDto[];
  refetchInstalledIntegrations: () => Promise<any>;
}

export default function InstalledIntegrations({
  searchQuery,
  list,
  refetchInstalledIntegrations
}: InstalledIntegrationsProps) {
  const allIntegrations = useMemo(() => {
    return list.map((x) => {
      const available = availableIntegrations[x.type];
      return {
        ...x,
        typeName: available?.name || '',
        description: available?.description || '',
        logo: available?.logo,
        tags: available?.tags || [],
        details: available?.installedDetails
      };
    });
  }, [list]);

  const fuseSearch = useMemo(
    () =>
      new Fuse(allIntegrations, {
        keys: ['name', 'typeName', 'description', 'tags'],
        threshold: 0.3
      }),
    [allIntegrations]
  );

  const integrations = useMemo(
    () =>
      searchQuery
        ? fuseSearch.search(searchQuery).map((result) => result.item)
        : allIntegrations,
    [fuseSearch, allIntegrations, searchQuery]
  );

  const [errorItem, setErrorItem] = useState<(typeof integrations)[number]>();
  const [detailsItem, setDetailsItem] = useState<
    (typeof integrations)[number] & { key: string }
  >();

  if (!integrations.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No installed integrations found"
        style={{
          gridColumnStart: 1,
          gridColumnEnd: -1
        }}
      />
    );
  }

  return (
    <>
      {integrations.map((x) => (
        <IntegrationCard
          key={x.id}
          integration={x}
          onErrorClick={() => setErrorItem(x)}
          onDetailsClick={() =>
            setDetailsItem({ ...x, key: crypto.randomUUID() })
          }
          refetchInstalledIntegrations={refetchInstalledIntegrations}
        />
      ))}
      <Modal
        open={!!detailsItem?.details}
        onCancel={() => setDetailsItem(undefined)}
        footer={null}
        width={800}
      >
        <div
          style={{ minHeight: 544, display: 'flex', flexDirection: 'column' }}
        >
          <Suspense
            fallback={
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            }
          >
            {detailsItem?.details ? (
              <detailsItem.details
                key={detailsItem.key}
                uniqueKey={detailsItem.key}
                integration={detailsItem}
              />
            ) : null}
          </Suspense>
        </div>
      </Modal>
      <Modal
        open={!!errorItem}
        footer={null}
        onCancel={() => setErrorItem(undefined)}
        width={560}
        destroyOnClose
      >
        <Title level={3} style={{ marginBottom: 8 }}>
          {errorItem?.name}
        </Title>

        <Alert
          type="error"
          showIcon
          message="The integration reported an error"
          description={
            <Paragraph style={{ marginTop: 8 }}>
              <Text code copyable>
                {errorItem?.error ??
                  'No additional error message was provided.'}
              </Text>
            </Paragraph>
          }
        />
      </Modal>
    </>
  );
}
